// Custom hook for managing RSVP operations
import { useState, useCallback } from 'react';
import { rsvpService } from '../firebase/rsvpService';

export const useRSVP = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Create a new RSVP
  const createRSVP = useCallback(async (rsvpData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Check if user already RSVP'd
      const existingCheck = await rsvpService.checkExistingRSVP(
        rsvpData.eventId, 
        rsvpData.email
      );

      if (existingCheck.exists) {
        // Update existing RSVP
        const updateResult = await rsvpService.updateRSVPStatus(
          existingCheck.data.id,
          rsvpData.status
        );

        if (updateResult.success) {
          setSuccess(true);
          return { 
            success: true, 
            updated: true, 
            message: 'Your RSVP has been updated!' 
          };
        } else {
          throw new Error(updateResult.error);
        }
      } else {
        // Create new RSVP
        const result = await rsvpService.createRSVP(rsvpData);
        
        if (result.success) {
          setSuccess(true);
          
          // ✅ Email sending is handled automatically by Cloud Function trigger
          // The onRSVPCreated function sends both user confirmation and admin notification
          
          return { 
            success: true, 
            updated: false, 
            message: 'Thank you for your RSVP! You will receive a confirmation email shortly.' 
          };
        } else {
          throw new Error(result.error);
        }
      }
    } catch (err) {
      console.error('Error in createRSVP:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get RSVPs for an event
  const getRSVPsByEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await rsvpService.getRSVPsByEvent(eventId);
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Error getting RSVPs:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if email already RSVP'd
  const checkExistingRSVP = useCallback(async (eventId, email) => {
    try {
      return await rsvpService.checkExistingRSVP(eventId, email);
    } catch (err) {
      console.error('Error checking existing RSVP:', err);
      return { exists: false };
    }
  }, []);

  // Reset states
  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    createRSVP,
    getRSVPsByEvent,
    checkExistingRSVP,
    loading,
    error,
    success,
    reset
  };
};